import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import API from '@/APIs/API';
import {BankVerificationResponse, BankVerificationState} from '@/APIs/types';
import {BankVerificationError} from '@/Agent/Common/BankVerificationError';
import {useBankVerification} from '@/Agent/Common/useBankVerification';
import {updateHubspotProsperDealBankingInfoProvidedUpfront} from '@/Agent/Quotes/deal';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import RootStore from '@/Core/mobx/RootStore';

interface PreReviewBankingScreenProps {
    ageniumContext: AgenuimContextType;
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onComplete: () => void;
}

const useStyles = makeStyles(() => ({
    title: {
        marginBottom: 16,
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '32px',
        lineHeight: '120%',
        color: '#456d69',
    },
    subtitle: {
        marginBottom: 32,
        fontFamily: 'FredokaRegular',
        fontSize: '16px',
        lineHeight: '160%',
        color: '#456d69',
    },
    field: {
        marginBottom: '24px',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {borderColor: '#174943'},
            '&:hover fieldset': {borderColor: '#174943'},
            '&.Mui-focused fieldset': {borderColor: '#174943'},
        },
    },
    fieldLabel: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#174943',
        marginBottom: '8px',
    },
    radioGroup: {
        flexDirection: 'row',
        gap: '24px',
        '& .MuiFormControlLabel-label': {
            fontFamily: 'FredokaRegular',
            fontSize: '16px',
            color: '#434343',
        },
    },
}));

type BankingErrors = {routingNumber?: string; accountNumber?: string; banking?: string};

export const PreReviewBankingScreen: React.FC<PreReviewBankingScreenProps> = ({
    ageniumContext,
    parameterStore,
    contactFormStore,
    onComplete,
}) => {
    const classes = useStyles();
    const {banking} = parameterStore;
    const [routingNumber, setRoutingNumber] = useState<string>(banking.routingNumber.value || '');
    const [accountNumber, setAccountNumber] = useState<string>(banking.accountNumber.value || '');
    const [accountType, setAccountType] = useState<'checking' | 'savings'>(
        (banking.accountType.value?.toLowerCase() as 'checking' | 'savings') || 'checking',
    );
    const [errors, setErrors] = useState<BankingErrors>({});
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    // True while bankVerificationFunction is awaiting GIACT (and any override call).
    // Guards against double-clicks of the parent Next button. We also flip
    // ageniumContext.setIsValidated(false) during the call so the parent re-renders and
    // actually disables the button — canProceedWithBanking alone is read lazily by the
    // parent and would otherwise see stale local state.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        verificationResult,
        overrideConfirmed,
        setOverrideConfirmed,
        isVerifying,
        verifyBankAccount,
        resetVerification,
        canProceed,
    } = useBankVerification();

    useEffect(() => {
        resetVerification();
        setErrors({});
        setHasAttemptedSubmit(false);
    }, [routingNumber, accountNumber, accountType, resetVerification]);

    const validateFields = useCallback((): BankingErrors => {
        const next: BankingErrors = {};
        if (!routingNumber) next.routingNumber = 'Routing number is required';
        else if (!/^\d{9}$/.test(routingNumber)) next.routingNumber = 'Routing number must be exactly 9 digits';
        if (!accountNumber) next.accountNumber = 'Account number is required';
        return next;
    }, [routingNumber, accountNumber]);

    const persistBankingOnSuccess = (verificationId: string | null) => {
        parameterStore.onValueChangedBanking(banking.routingNumber, routingNumber);
        parameterStore.onValueChangedBanking(banking.accountNumber, accountNumber);
        parameterStore.onValueChangedBanking(banking.accountType, accountType.toUpperCase());

        parameterStore.approval.bankingGiactPassPreApp.setValue(true);
        parameterStore.approval.bankingCollectedPreApp.setValue(true);
        const alreadyTracked = parameterStore.approval.bankingProvidedUpfront.value === true;
        parameterStore.approval.bankingProvidedUpfront.setValue(true);

        contactFormStore.saveForm();

        if (!alreadyTracked) {
            updateHubspotProsperDealBankingInfoProvidedUpfront('Yes').catch((error) => {
                console.error('Failed to update Hubspot deal with banking info:', error);
            });
        }

        if (verificationId) {
            console.log(`Pre-review banking GIACT verified (id=${verificationId})`);
        }
    };

    const performBankVerification = useCallback(async (): Promise<{success: boolean; error?: string}> => {
        setHasAttemptedSubmit(true);

        const fieldErrors = validateFields();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return {success: false, error: 'Please complete all banking fields'};
        }

        // Already verified and result allows proceeding
        if (verificationResult && canProceed()) {
            if (verificationResult.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE) {
                // The override call records the agent's acknowledgement for audit. If it fails we
                // must NOT mark banking as GIACT-passed or advance — keep the user on this screen
                // and surface the failure so they can retry.
                try {
                    await API.OverrideBankVerification({
                        verificationId: verificationResult.verificationId,
                        accountHash: (verificationResult as BankVerificationResponse).accountHash || '',
                        reason: 'Agent confirmed bank information is correct after speaking with client',
                    });
                } catch (e) {
                    console.error('Bank verification override call failed:', e);
                    return {
                        success: false,
                        error: 'Could not record your override acknowledgement. Please try again.',
                    };
                }
            }
            persistBankingOnSuccess(verificationResult.verificationId);
            return {success: true};
        }

        const accountTypeNumeric: 1 | 2 = accountType === 'checking' ? 1 : 2;
        const customerEmail = parameterStore.contact || contactFormStore.email || '';
        const firstName = parameterStore.approval.firstName.value || parameterStore.walkthroughRawData?.first_name;
        const lastName = parameterStore.approval.lastName.value || parameterStore.walkthroughRawData?.last_name;
        const ageniumCaseId =
            ageniumContext.applicationData?.find((t: any) => t.key === 'Case_CaseID')?.value ||
            ageniumContext.applicationId;

        const {success, result} = await verifyBankAccount({
            routingNumber,
            accountNumber,
            accountType: accountTypeNumeric,
            customerEmail,
            customerFirstName: firstName || undefined,
            customerLastName: lastName || undefined,
            formId: parameterStore.formid,
            ageniumCaseId: ageniumCaseId || undefined,
        });

        if (success && result?.state === BankVerificationState.PASS) {
            persistBankingOnSuccess(result.verificationId);
            return {success: true};
        }

        // Verification did not pass — keep the result visible (BankVerificationError will render)
        // PREVENT_NEXT_WITH_OVERRIDE: the override checkbox will reveal; user clicks Next again to retry through canProceed branch
        const message =
            (result as any)?.failureMessage ||
            'Bank verification failed. Please correct the information or speak with the client to confirm.';
        return {success: false, error: message};
    }, [
        accountNumber,
        accountType,
        ageniumContext.applicationData,
        ageniumContext.applicationId,
        canProceed,
        contactFormStore,
        parameterStore,
        routingNumber,
        validateFields,
        verificationResult,
        verifyBankAccount,
    ]);

    // Hook the parent fixed Next button into our verification flow.
    useEffect(() => {
        (ageniumContext as any).bankVerificationFunction = async () => {
            setIsSubmitting(true);
            ageniumContext.setIsValidated(false);
            try {
                const result = await performBankVerification();
                if (result.success) {
                    onComplete();
                }
                return result;
            } finally {
                setIsSubmitting(false);
                ageniumContext.setIsValidated(true);
            }
        };
        (ageniumContext as any).canProceedWithBanking = () => {
            if (isSubmitting || isVerifying) return false;
            const fieldErrors = validateFields();
            if (Object.keys(fieldErrors).length > 0) return false;
            if (verificationResult) return canProceed();
            return true; // not yet verified — let user press Next to trigger verification
        };
        // The parent's Next button reads ageniumContext.isValidated. We want it always-true here
        // because validity is enforced via canProceedWithBanking instead.
        ageniumContext.setIsValidated(true);

        return () => {
            delete (ageniumContext as any).bankVerificationFunction;
            delete (ageniumContext as any).canProceedWithBanking;
        };
    }, [
        ageniumContext,
        canProceed,
        isSubmitting,
        isVerifying,
        onComplete,
        performBankVerification,
        validateFields,
        verificationResult,
    ]);

    const handleRoutingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 9);
        setRoutingNumber(value);
    };
    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 17);
        setAccountNumber(value);
    };
    const handleAccountTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAccountType(e.target.value as 'checking' | 'savings');
    };

    return (
        <Box>
            <Typography component="h1" className={classes.title}>
                Let's verify your bank information
            </Typography>
            <Typography className={classes.subtitle}>
                Before we send your application for a decision, we need to verify the bank account that will be used for
                premium payments.
            </Typography>

            <Box className={classes.field}>
                <Typography className={classes.fieldLabel}>Bank Routing Number</Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="9-digit routing number"
                    value={routingNumber}
                    onChange={handleRoutingNumberChange}
                    error={hasAttemptedSubmit && !!errors.routingNumber}
                    helperText={hasAttemptedSubmit ? errors.routingNumber : ''}
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*', maxLength: 9, autoComplete: 'off'}}
                />
            </Box>

            <Box className={classes.field}>
                <Typography className={classes.fieldLabel}>Bank Account Number</Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Account number"
                    value={accountNumber}
                    onChange={handleAccountNumberChange}
                    error={hasAttemptedSubmit && !!errors.accountNumber}
                    helperText={hasAttemptedSubmit ? errors.accountNumber : ''}
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*', maxLength: 17, autoComplete: 'off'}}
                />
            </Box>

            <Box className={classes.field}>
                <FormControl component="fieldset">
                    <FormLabel component="legend" className={classes.fieldLabel}>
                        Account Type
                    </FormLabel>
                    <RadioGroup value={accountType} onChange={handleAccountTypeChange} className={classes.radioGroup}>
                        <FormControlLabel value="checking" control={<Radio color="primary" />} label="Checking" />
                        <FormControlLabel value="savings" control={<Radio color="primary" />} label="Savings" />
                    </RadioGroup>
                </FormControl>
            </Box>

            {hasAttemptedSubmit && errors.banking && (
                <FormHelperText error style={{marginBottom: '16px', fontSize: '14px'}}>
                    {errors.banking}
                </FormHelperText>
            )}

            {verificationResult && (
                <Box style={{marginBottom: '16px'}}>
                    <BankVerificationError
                        verificationResult={verificationResult}
                        overrideConfirmed={overrideConfirmed}
                        onOverrideChange={(checked) => {
                            setOverrideConfirmed(checked);
                            if (!checked) {
                                RootStore.uiStore.showSnackbar(
                                    'Override removed. You will need to acknowledge again to proceed.',
                                    'info',
                                );
                            }
                        }}
                    />
                </Box>
            )}

            {isVerifying && (
                <Box display="flex" alignItems="center" style={{gap: '8px', marginBottom: '16px'}}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Verifying bank account...</Typography>
                </Box>
            )}
        </Box>
    );
};

export default PreReviewBankingScreen;
